import React from "react";
import clsx from "clsx";
import _ from "lodash";

import Grid from "@material-ui/core/Grid";
import withStyles from "@material-ui/core/styles/withStyles";
import Skeleton from "@material-ui/lab/Skeleton";
import {Chip} from "@material-ui/core";

import AssetCard from "./components/AssetCard";
import PageLoadingIndicator from "../../RiskProfiling/components/PageLoadingIndicator";
import StepName from "../../RiskProfiling/components/StepContent/components/step/DynamicStep/components/StepName";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import CreateAssetModal from "../CreateAssetModal";
import {toGermanFormat} from "../../../utils/numberFormater";
import {ASSETS_LIABILITIES_ACTIONS, ASSETS_LIABILITIES_CONFIG, FILTER_OPTIONS, LOADING_MSG} from "../constants";
import CategoryHeader from "./components/CategoryHeader";
import InfoIcon from "../InfoIcon";
import SelectFormElementV2 from "../../RiskProfiling/components/StepContent/components/formElement/SelectFormElementV2";
import {getFilteredCategories} from "../utils";
import InfoCard from "../../RiskProfiling/components/StepContent/components/step/DynamicStep/components/InfoCard";
import WarningTooltip from "../../../components/WarningTooltip";
import {useChartsSettingsRegistryContext} from "../../CustomerDashboardV2/components_v2/Registry";

import styles from "./styles";

const FILTER_SELECT_OPTIONS = Object.values(FILTER_OPTIONS);

const CreateAssetComponent = ({classes, customerId, objectType, itemsForLinks, assetTypeConfig, loading, onUpdate}) => {
  const [addAssetModalOpen, setAddAssetModalOpen] = React.useState(false);

  const closeAddAssetModal = () => setAddAssetModalOpen(false);

  const assetCreateHandler = (categoryId, asset, close=false) => {
    if(close) closeAddAssetModal();

    onUpdate(ASSETS_LIABILITIES_ACTIONS.CREATE, categoryId, asset);
  };

  return (
    <Grid item xs={12} sm={'auto'} data-id={`new-${assetTypeConfig.singleLabel}-btn-container`}>
      <PrimaryButton
        text={<>
          <span className={classes.addBtnShort}>hinzufügen</span>
          <span className={classes.addBtnLong}>{assetTypeConfig.addLabel}</span>
        </>}
        icon={<i className={'fal fa-plus'} />}
        onButtonClick={() => setAddAssetModalOpen(true)}
        disabled={loading}
        style={{width: '100%'}}
      />
      <CreateAssetModal
        open={addAssetModalOpen}
        onModalClose={closeAddAssetModal}
        onAssetCreate={assetCreateHandler}
        customerId={customerId}
        objectType={objectType}
        itemsForLinks={itemsForLinks}
      />
    </Grid>
  );
};

const List = (props) => {
  const {
    classes,
    customerId,
    onUpdate,
    stepName,
    helpText,
    totalColor,
    data,
    loading,
    extraDataLoading,
    onDepotsUpdate,
    objectType,
    showNewBadge,
  } = props;

  const settingsRegistry = useChartsSettingsRegistryContext();
  const assetTypeConfig = ASSETS_LIABILITIES_CONFIG[objectType];

  const [selectedFilter, setSelectedFilter] = React.useState(FILTER_OPTIONS.active.value);

  React.useEffect(() => {
    // Note: in advisory we don't have ChartsSettingsRegistryContext provider
    if(settingsRegistry) {
      settingsRegistry.set(['other_assets', `${objectType}_filter`], selectedFilter)
    }
  }, [selectedFilter]);

  const filterSelectOptions = React.useMemo(() => {
    return FILTER_SELECT_OPTIONS.map(o => {
      const info = assetTypeConfig.filterInfos[o.value];
      return !!info ? {...o, label: <>{o.label}<WarningTooltip title={info} /></>} : o;
    }
  )
  }, [objectType]);

  const filteredItems = React.useMemo(() => {
    if (selectedFilter === FILTER_OPTIONS.all.value){
      return data.items;
    }

    return getFilteredCategories(data.items, selectedFilter);
  }, [selectedFilter, data.items]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} className={classes.stickyHeadline}>
        <Grid container spacing={2} alignItems={'center'}>
          <Grid item className={classes.stepName}>
            <StepName name={stepName}/><InfoIcon showNewBadge={showNewBadge} />
          </Grid>
          <Grid item>
            {(loading || extraDataLoading)
              ? <Skeleton width={50} />
              : <Chip
                  className={clsx(classes.totalValueChip, data.total_value > 0 && totalColor)}
                  label={toGermanFormat(data.total_value, '', ' €')}
                />
            }
          </Grid>
          <Grid item xs={12} sm={'auto'} style={{marginLeft: 'auto'}}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={'auto'}>
                <SelectFormElementV2
                  value={selectedFilter}
                  options={filterSelectOptions}
                  onChange={setSelectedFilter}
                  custom_classes={{inputRoot: classes.filterSelect}}
                  newDesign
                />
              </Grid>
              <CreateAssetComponent
                classes={classes}
                customerId={customerId}
                objectType={objectType}
                itemsForLinks={data.itemsForLinks}
                assetTypeConfig={assetTypeConfig}
                loading={loading}
                onUpdate={onUpdate}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {!!helpText && <Grid item xs={12}>{helpText}</Grid>}

      {loading
        ? <PageLoadingIndicator loadingMsg={LOADING_MSG} />
        : <>
          {/* children here is a workaround to pass "no liabilities" checkbox before list of expired liabilities */}
          {props.children}
          {_.isEmpty(filteredItems)
            ? !!props.children
              ? null
              : <Grid item xs={12}><InfoCard isNewDesign>{assetTypeConfig.noFilteredItems[selectedFilter]}</InfoCard></Grid>
            : filteredItems.map((category) => (
              <Grid key={category.id} item xs={12}>
                <Grid container spacing={1}>
                  <CategoryHeader category={category} objectType={objectType}/>
                  {category.assets.map((asset) => (
                    <AssetCard
                      key={`${asset.type_id}-${asset.id}`}
                      customerId={customerId}
                      groupId={category.id}
                      categoryIcon={category.icon}
                      asset={asset}
                      onUpdate={onUpdate}
                      onDepotsUpdate={onDepotsUpdate}
                      objectType={objectType}
                      itemsForLinks={data.itemsForLinks}
                    />
                  ))}
                </Grid>
              </Grid>
            ))
          }
        </>
      }
      {!loading && !!extraDataLoading && <PageLoadingIndicator />}
    </Grid>
  )
};

export default withStyles(styles)(List);